/**
 * API Service for 獅聲心語 (Lion Voice)
 * Connects to the Lions Magazine Django backend
 */
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://lionmagazine-backend-139572186833.asia-southeast1.run.app/api';
const AUTH_BASE_URL = import.meta.env.VITE_AUTH_URL || 'https://d303hub-dev-139572186833.asia-southeast1.run.app/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach token to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401: retry GET anonymously once; redirect other methods to login
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      if (originalRequest && originalRequest.method === 'get' && !originalRequest._retried) {
        originalRequest._retried = true;
        delete originalRequest.headers['Authorization'];
        return apiClient(originalRequest);
      }

      if (originalRequest && originalRequest.method !== 'get') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/auth/callback') {
          window.location.replace('https://portal.lionsclubs.org.hk');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password, localLogin = false) => {
    const url = localLogin
      ? `${API_BASE_URL}/auth/login/`
      : `${AUTH_BASE_URL}/auth/login/`;
    const response = await axios.post(url, { username, password });
    const { token, ...userInfo } = response.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(userInfo));
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    window.location.replace('https://portal.lionsclubs.org.hk');
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp && Date.now() / 1000 > payload.exp) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          return false;
        }
      }
    } catch {
      // Not a JWT — treat as valid
    }
    return true;
  },

  fetchMe: async () => {
    const response = await apiClient.get('/auth/me/');
    localStorage.setItem('auth_user', JSON.stringify(response.data));
    return response.data;
  },

  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem('auth_user') || 'null');
    } catch {
      return null;
    }
  },
};

// 獅聲心語 (Lion Voice) API
export const lionVoiceAPI = {
  getAll: async (params) => {
    const response = await apiClient.get('/lionvoice/episodes/', {
      params: { page_size: 100, ...params },
    });
    const data = response.data;

    // Unpaginated response (local backend) or single page with all results
    if (Array.isArray(data)) {
      return { ...response, data };
    }
    if (!data.next) {
      return response;
    }

    // Paginated production API — fetch remaining pages
    const results = [...(data.results || [])];
    let nextUrl = data.next;
    while (nextUrl) {
      const pageResponse = await apiClient.get(nextUrl);
      const pageData = pageResponse.data;
      results.push(...(pageData.results || []));
      nextUrl = pageData.next;
    }
    return {
      ...response,
      data: { ...data, results, count: results.length, next: null, previous: null },
    };
  },
  getById: (id) => apiClient.get(`/lionvoice/episodes/${id}/`),
};
